import "@/App.css";
import { BrowserRouter, Routes, Route, useParams, Navigate, useLocation } from "react-router-dom";
import { useEffect } from "react";
import Landing from "./pages/Landing";
import Login from "./pages/Login";
import Signup from "./pages/Signup";
import NotFound from "./pages/NotFound";
import Join from "./pages/Join";
import AdvisorSignup from "./pages/AdvisorSignup";
import ForAdvisors from "./pages/ForAdvisors";
import { WhatsAppFloat } from "./components/WhatsAppFloat";
import { DAVID_ENABLED } from "./lib/features";
import AdvisorDashboard from "./pages/AdvisorDashboard";
import AdvisorConfirm from "./pages/AdvisorConfirm";
import GetStartedFunder from "./pages/GetStartedFunder";
import ForgotPassword from "./pages/ForgotPassword";
import VerifyEmail from "./pages/VerifyEmail";
import Unsubscribe from "./pages/Unsubscribe";
import WelcomeSnapshot from "./pages/WelcomeSnapshot";
import Verdict from "./pages/Verdict";
import ResetPassword from "./pages/ResetPassword";
import GetStartedQuiz from "./pages/GetStartedQuiz";
import CapitalReadinessQuiz from "./pages/CapitalReadinessQuiz";
import GetStartedProject from "./pages/GetStartedProject";
import ProjectIntake from "./pages/ProjectIntake";
import ProjectReport from "./pages/ProjectReport";
import AuthCallback from "./pages/AuthCallback";
import Onboarding from "./pages/Onboarding";
import PrivacyPolicy from "./pages/PrivacyPolicy";
import FAQ from "./pages/FAQ";
import TermsOfService from "./pages/TermsOfService";
import ForProjects from "./pages/ForProjects";
import ForBusiness from "./pages/ForBusiness";
import ForFunders from "./pages/ForFunders";
import VentureStudios from "./pages/VentureStudios";
import StudioDashboard from "./pages/StudioDashboard";
import FounderDashboard from "./pages/FounderDashboard";
import FounderPathway from "./pages/FounderPathway";
import FounderApply from "./pages/FounderApply";
import FounderFunderPage from "./pages/FounderFunderPage";
import FunderTerminal from "./pages/FunderTerminal";
import FunderSaved from "./pages/FunderSaved";
import FunderSMEDetail from "./pages/FunderSMEDetail";
import FunderAuditLog from "./pages/FunderAuditLog";
import FunderMandate from "./pages/FunderMandate";
import FunderAnalytics from "./pages/FunderAnalytics";
import FunderAlerts from "./pages/FunderAlerts";
import FunderInbox from "./pages/FunderInbox";
import FunderPipeline from "./pages/FunderPipeline";
import FunderBilling from "./pages/FunderBilling";
import FounderTeam from "./pages/FounderTeam";
import FounderInterests from "./pages/FounderInterests";
import FounderReferrals from "./pages/FounderReferrals";
import AdminSupportTickets from "./pages/AdminSupportTickets";
import AdminDavidChats from "./pages/AdminDavidChats";
import AdminDavidInsights from "./pages/AdminDavidInsights";
import AdminHVCTracker from "./pages/AdminHVCTracker";
import AdminQuizLeads from "./pages/AdminQuizLeads";
import AdminAutomatedEmails from "./pages/AdminAutomatedEmails";
import AdminTenants from "./pages/AdminTenants";
import AdminPartners from "./pages/AdminPartners";
import AdminRMs from "./pages/AdminRMs";
import RMDashboard from "./pages/RMDashboard";
import RMTraining from "./pages/RMTraining";
import RMTrainingModule from "./pages/RMTrainingModule";
import RMTrainingQuiz from "./pages/RMTrainingQuiz";
import AdminExpertSessions from "./pages/AdminExpertSessions";
import AdminMasterclasses from "./pages/AdminMasterclasses";
import AdminAcceptInvite from "./pages/AdminAcceptInvite";
import PageViewTracker from "./components/PageViewTracker";
import AdminClientErrors from "./pages/AdminClientErrors";
import AssistantWidget from "./components/AssistantWidget";
import AdminSignup from "./pages/AdminSignup";
import AdminDashboard from "./pages/AdminDashboard";
import AdminAnalytics from "./pages/AdminAnalytics";
import AdminMonthlyReport from "./pages/AdminMonthlyReport";
import JoinTeam from "./pages/JoinTeam";
import AnalystSupport from "./pages/AnalystSupport";
import YourRM from "./pages/YourRM";
import InvestorReadiness from "./pages/InvestorReadiness";
import Saccos from "./pages/Saccos";
import Discovery from "./pages/Discovery";
import DiscoveryReschedule from "./pages/DiscoveryReschedule";
import AdminDiscoverySessions from "./pages/AdminDiscoverySessions";
import AdminCoupons from "./pages/AdminCoupons";
import MasterclassInterest from "./pages/MasterclassInterest";
import AdminBusinesses from "./pages/AdminBusinesses";
import AdminBusinessNew from "./pages/AdminBusinessNew";
import AdminBusinessDetail from "./pages/AdminBusinessDetail";
import AdminFunders from "./pages/AdminFunders";
import AdminFunderApplications from "./pages/AdminFunderApplications";
import AdminAlerts from "./pages/AdminAlerts";
import AdminAccelerators from "./pages/AdminAccelerators";
import AdminUsers from "./pages/AdminUsers";
import AdminVerifiedReadiness from "./pages/AdminVerifiedReadiness";
import FounderClaim from "./pages/FounderClaim";
import { NDA } from "./pages/LegalPages";
import LogoPreview from "./pages/LogoPreview";
import FunderOnboarding from "./pages/FunderOnboarding";
import FunderPendingApproval from "./pages/FunderPendingApproval";
import Pricing from "./pages/Pricing";
import WhyDocumentsMatter from "./pages/WhyDocumentsMatter";
import AdvisorBooking from "./pages/AdvisorBooking";
import Masterclasses from "./pages/Masterclasses";
import SampleReadinessReport from "./pages/SampleReadinessReport";
import DetailedReport from "./pages/DetailedReport";
import PublicReadinessReport from "./pages/PublicReadinessReport";
import FunderTutorial from "./pages/FunderTutorial";
import FounderTutorial from "./pages/FounderTutorial";
import FounderSupport from "./pages/FounderSupport";
import FounderActivity from "./pages/FounderActivity";
import SsoConsume from "./pages/SsoConsume";
import InstallPrompt from "./components/InstallPrompt";
import { Toaster } from "./components/ui/sonner";
import ProtectedRoute from "./components/ProtectedRoute";
import { ClinqLogoStyles } from "./components/ClinqLogo";
import { SectionEyebrowStyles } from "./components/landing/SectionEyebrow";
import { AuthProvider } from "./contexts/AuthContext";
import { GoogleOAuthProvider } from "@react-oauth/google";
import { initErrorReporter } from "./lib/errorReporter";
import TenantApp, { TenantPathApp } from "./pages/tenant/TenantApp";
import Handoff from "./pages/Handoff";
import { detectHostTenant } from "./lib/tenant";

const HOST_TENANT = detectHostTenant();

// David + WhatsApp belong to Clinq, not to co-branded tenant portals.
function ClinqOnly({ children }) {
  const { pathname } = useLocation();
  if (HOST_TENANT || pathname.startsWith("/t/")) return null;
  return children;
}

// REMINDER: DO NOT HARDCODE THE URL, OR ADD ANY FALLBACKS OR REDIRECT URLS, THIS BREAKS THE AUTH
const GOOGLE_CLIENT_ID = process.env.REACT_APP_GOOGLE_CLIENT_ID;

// /founder/services was retired — send old bookmarks / email deep-links to
// the Prep sibling app, which now builds documents for founders.
function ExternalPrepRedirect() {
  useEffect(() => {
    window.location.replace("https://prep.clinq.africa/?utm_source=clinq");
  }, []);
  return null;
}

// Activate once at module load — outside the component so HMR doesn't double-bind.
initErrorReporter();

function App() {
  if (HOST_TENANT) {
    return (
      <div className="App">
        <Toaster richColors position="top-right" closeButton />
        <BrowserRouter><TenantApp sub={HOST_TENANT} basePath="" /></BrowserRouter>
      </div>
    );
  }
  return (
    <div className="App">
      <ClinqLogoStyles />
      <SectionEyebrowStyles />
      <InstallPrompt />
      <Toaster richColors position="top-right" closeButton />
      <GoogleOAuthProvider clientId={GOOGLE_CLIENT_ID}>
      <BrowserRouter>
        <AuthProvider>
          <PageViewTracker />
          <Routes>
            <Route path="/" element={<Landing />} />
            <Route path="/t/:sub/*" element={<TenantPathApp />} />
            <Route path="/handoff" element={<Handoff />} />
            <Route path="/privacy" element={<Navigate to="/privacy-policy" replace />} />
            <Route path="/privacy-policy" element={<PrivacyPolicy />} />
            <Route path="/faq" element={<FAQ />} />
            <Route path="/terms" element={<TermsOfService />} />
            <Route path="/nda" element={<NDA />} />
            <Route path="/__logo-preview" element={<LogoPreview />} />
            <Route path="/login" element={<Login />} />
            <Route path="/signup" element={<Signup />} />
            <Route path="/forgot-password" element={<ForgotPassword />} />
            <Route path="/verify-email/:token" element={<VerifyEmail />} />
            <Route path="/unsubscribe" element={<Unsubscribe />} />
            <Route path="/reset-password" element={<ResetPassword />} />
            <Route path="/get-started" element={<GetStartedQuiz />} />
            <Route path="/capital-readiness-quiz" element={<CapitalReadinessQuiz />} />
            <Route path="/get-started-funder" element={<GetStartedFunder />} />
            <Route path="/get-started-project" element={<GetStartedProject />} />
            <Route path="/join" element={<Join />} />
            <Route path="/join-team/:token" element={<JoinTeam />} />
            <Route path="/analyst-support" element={<AnalystSupport />} />
            <Route path="/your-rm" element={<YourRM />} />
            <Route path="/investor-readiness" element={<InvestorReadiness />} />
            <Route path="/saccos" element={<Saccos />} />
            <Route path="/discovery" element={<Discovery />} />
            <Route path="/discovery/reschedule/:token" element={<DiscoveryReschedule />} />
            <Route path="/masterclass-interest" element={<MasterclassInterest />} />
            <Route path="/get-analyst" element={<Navigate to="/analyst-support" replace />} />
            <Route path="/join-advisor" element={<AdvisorSignup />} />
            <Route path="/for-advisors" element={<ForAdvisors />} />
            <Route path="/advisor/confirm/:token" element={<AdvisorConfirm />} />
            <Route path="/advisor/dashboard" element={
              <ProtectedRoute role="advisor"><AdvisorDashboard /></ProtectedRoute>
            } />
            <Route path="/rm/dashboard" element={
              <ProtectedRoute role="rm"><RMDashboard /></ProtectedRoute>
            } />
            <Route path="/rm/training" element={
              <ProtectedRoute role={["rm", "admin"]}><RMTraining /></ProtectedRoute>
            } />
            <Route path="/rm/training/module/:id" element={
              <ProtectedRoute role={["rm", "admin"]}><RMTrainingModule /></ProtectedRoute>
            } />
            <Route path="/rm/training/module/:id/quiz" element={
              <ProtectedRoute role={["rm", "admin"]}><RMTrainingQuiz /></ProtectedRoute>
            } />
            <Route path="/rm/training/final" element={
              <ProtectedRoute role={["rm", "admin"]}><RMTrainingQuiz mode="final" /></ProtectedRoute>
            } />
            <Route path="/pricing" element={<Pricing />} />
            <Route path="/why-documents-matter" element={<WhyDocumentsMatter />} />
            <Route path="/advisor" element={<AdvisorBooking />} />
            <Route path="/sample-report" element={<SampleReadinessReport />} />
            <Route path="/r/:token" element={<PublicReadinessReport />} />
            <Route path="/verify/:verifyId" element={<VerifyRedirect />} />
            <Route path="/auth/callback" element={<AuthCallback />} />
            <Route path="/sso/consume" element={<SsoConsume />} />
            <Route path="/admin/signup" element={<AdminSignup />} />
            <Route path="/founder/claim" element={<FounderClaim />} />
            {/* /welcome was retired (Aug 2026) — its five questions duplicated
                the onboarding Business Snapshot. One flow now: signup → NDA →
                /onboarding, where the snapshot collects everything once. */}
            <Route path="/welcome" element={<Navigate to="/onboarding" replace />} />
            <Route path="/onboarding" element={
              <ProtectedRoute role="founder"><Onboarding /></ProtectedRoute>
            } />
            <Route path="/welcome-snapshot" element={
              <ProtectedRoute role="founder"><WelcomeSnapshot /></ProtectedRoute>
            } />
            <Route path="/verdict" element={
              <ProtectedRoute role="founder"><Verdict /></ProtectedRoute>
            } />
            <Route path="/project/intake" element={
              <ProtectedRoute role="founder"><ProjectIntake /></ProtectedRoute>
            } />
            <Route path="/project/report" element={
              <ProtectedRoute role="founder"><ProjectReport /></ProtectedRoute>
            } />
            <Route path="/founder/report" element={<Navigate to="/founder/readiness-report" replace />} />
            <Route path="/founder/readiness-report" element={
              <ProtectedRoute role="founder"><DetailedReport /></ProtectedRoute>
            } />
            <Route path="/masterclasses" element={
              <ProtectedRoute role="founder"><Masterclasses /></ProtectedRoute>
            } />
            <Route path="/founder/journey" element={<Navigate to="/onboarding" replace />} />
            <Route path="/founder/pathway" element={
              <ProtectedRoute role="founder" skipPathway><FounderPathway /></ProtectedRoute>
            } />
            <Route path="/founder/apply" element={
              <ProtectedRoute role="founder"><FounderApply /></ProtectedRoute>
            } />
            <Route path="/founder/apply/:matchId" element={
              <ProtectedRoute role="founder"><FounderFunderPage /></ProtectedRoute>
            } />
            <Route path="/founder/dashboard" element={
              <ProtectedRoute role="founder"><FounderDashboard /></ProtectedRoute>
            } />
            <Route path="/founder/team" element={
              <ProtectedRoute role="founder"><FounderTeam /></ProtectedRoute>
            } />
            {/* /founder/services was retired in Feb 2026 — Clinq Prep
                (prep.clinq.africa) builds these documents instead. Redirect
                old bookmarks / email links to the Prep app. */}
            <Route path="/founder/services" element={<ExternalPrepRedirect />} />
            <Route path="/founder/interests" element={
              <ProtectedRoute role="founder"><FounderInterests /></ProtectedRoute>
            } />
            {/* /founder/outreach and /founder/accelerators were removed in Feb 2026.
                Redirect old bookmarks to the dashboard so founders don't hit a blank page. */}
            <Route path="/founder/outreach" element={<Navigate to="/founder/dashboard" replace />} />
            <Route path="/founder/accelerators" element={<Navigate to="/founder/dashboard" replace />} />
            <Route path="/founder/referrals" element={
              <ProtectedRoute role="founder"><FounderReferrals /></ProtectedRoute>
            } />
            <Route path="/founder/tutorial" element={
              <ProtectedRoute role="founder"><FounderTutorial /></ProtectedRoute>
            } />
            <Route path="/founder/support" element={
              <ProtectedRoute role="founder"><FounderSupport /></ProtectedRoute>
            } />
            <Route path="/founder/activity" element={
              <ProtectedRoute role="founder"><FounderActivity /></ProtectedRoute>
            } />
            <Route path="/funder/onboarding" element={
              <ProtectedRoute role="funder" skipFunderOnboarding skipFunderApproval><FunderOnboarding /></ProtectedRoute>
            } />
            <Route path="/funder/approval" element={
              <ProtectedRoute role="funder" skipFunderApproval skipFunderOnboarding><FunderPendingApproval /></ProtectedRoute>
            } />
            <Route path="/funder/tutorial" element={
              <ProtectedRoute role="funder"><FunderTutorial /></ProtectedRoute>
            } />
            <Route path="/funder/terminal" element={
              <ProtectedRoute role="funder"><FunderTerminal /></ProtectedRoute>
            } />
            <Route path="/funder/saved" element={
              <ProtectedRoute role="funder"><FunderSaved /></ProtectedRoute>
            } />
            <Route path="/funder/sme/:id" element={
              <ProtectedRoute role={["funder", "admin"]}><FunderSMEDetail /></ProtectedRoute>
            } />
            <Route path="/funder/audit" element={
              <ProtectedRoute role="funder"><FunderAuditLog /></ProtectedRoute>
            } />
            <Route path="/funder/mandate" element={
              <ProtectedRoute role="funder"><FunderMandate /></ProtectedRoute>
            } />
            <Route path="/funder/analytics" element={
              <ProtectedRoute role="funder"><FunderAnalytics /></ProtectedRoute>
            } />
            <Route path="/funder/billing" element={
              <ProtectedRoute role="funder"><FunderBilling /></ProtectedRoute>
            } />
            <Route path="/funder/alerts" element={
              <ProtectedRoute role="funder"><FunderAlerts /></ProtectedRoute>
            } />
            <Route path="/funder/inbox" element={
              <ProtectedRoute role="funder"><FunderInbox /></ProtectedRoute>
            } />
            <Route path="/funder/pipeline" element={
              <ProtectedRoute role="funder"><FunderPipeline /></ProtectedRoute>
            } />
            <Route path="/admin/dashboard" element={
              <ProtectedRoute role="admin"><AdminDashboard /></ProtectedRoute>
            } />
            <Route path="/admin/businesses" element={
              <ProtectedRoute role="admin"><AdminBusinesses /></ProtectedRoute>
            } />
            <Route path="/admin/businesses/new" element={
              <ProtectedRoute role="admin"><AdminBusinessNew /></ProtectedRoute>
            } />
            <Route path="/admin/businesses/:id" element={
              <ProtectedRoute role="admin"><AdminBusinessDetail /></ProtectedRoute>
            } />
            <Route path="/admin/funders" element={
              <ProtectedRoute role="admin"><AdminFunders /></ProtectedRoute>
            } />
            <Route path="/admin/funder-applications" element={
              <ProtectedRoute role="admin"><AdminFunderApplications /></ProtectedRoute>
            } />
            <Route path="/admin/alerts" element={
              <ProtectedRoute role="admin"><AdminAlerts /></ProtectedRoute>
            } />
            <Route path="/admin/accelerators" element={
              <ProtectedRoute role="admin"><AdminAccelerators /></ProtectedRoute>
            } />
            <Route path="/admin/users" element={
              <ProtectedRoute role="admin"><AdminUsers /></ProtectedRoute>
            } />
            <Route path="/admin/verified-readiness" element={
              <ProtectedRoute role="admin"><AdminVerifiedReadiness /></ProtectedRoute>
            } />
            <Route path="/admin/support-tickets" element={
              <ProtectedRoute role="admin"><AdminSupportTickets /></ProtectedRoute>
            } />
            <Route path="/admin/david-chats" element={
              <ProtectedRoute role="admin"><AdminDavidChats /></ProtectedRoute>
            } />
            <Route path="/admin/david-insights" element={
              <ProtectedRoute role="admin"><AdminDavidInsights /></ProtectedRoute>
            } />
            <Route path="/admin/hvc-tracker" element={
              <ProtectedRoute role={["admin", "advisor"]}><AdminHVCTracker /></ProtectedRoute>
            } />
            <Route path="/admin/quiz-leads" element={
              <ProtectedRoute role={["admin", "advisor"]}><AdminQuizLeads /></ProtectedRoute>
            } />
            <Route path="/admin/automated-emails" element={
              <ProtectedRoute role="admin"><AdminAutomatedEmails /></ProtectedRoute>
            } />
            <Route path="/admin/partners" element={
              <ProtectedRoute role="admin"><AdminPartners /></ProtectedRoute>
            } />
            <Route path="/admin/tenants" element={
              <ProtectedRoute role="admin"><AdminTenants /></ProtectedRoute>
            } />
            <Route path="/admin/rms" element={
              <ProtectedRoute role="admin"><AdminRMs /></ProtectedRoute>
            } />
            <Route path="/admin/analytics" element={
              <ProtectedRoute role="admin"><AdminAnalytics /></ProtectedRoute>
            } />
            <Route path="/admin/monthly-report" element={
              <ProtectedRoute role="admin"><AdminMonthlyReport /></ProtectedRoute>
            } />
            <Route path="/admin/discovery-sessions" element={
              <ProtectedRoute role="admin"><AdminDiscoverySessions /></ProtectedRoute>
            } />
            <Route path="/admin/coupons" element={
              <ProtectedRoute role="admin"><AdminCoupons /></ProtectedRoute>
            } />
            <Route path="/admin/expert-sessions" element={
              <ProtectedRoute role="admin"><AdminExpertSessions /></ProtectedRoute>
            } />
            <Route path="/admin/masterclasses" element={
              <ProtectedRoute role="admin"><AdminMasterclasses /></ProtectedRoute>
            } />
            <Route path="/admin/client-errors" element={
              <ProtectedRoute role="admin"><AdminClientErrors /></ProtectedRoute>
            } />
            {/* Public: one-time admin invite links land here */}
            <Route path="/admin/accept-invite" element={<AdminAcceptInvite />} />
            <Route path="/for-business" element={<ForBusiness />} />
            <Route path="/for-projects" element={<ForProjects />} />
            <Route path="/for-funders" element={<ForFunders />} />
            <Route path="/venture-studios" element={<VentureStudios />} />
            <Route path="/studio/dashboard" element={
              <ProtectedRoute role="studio"><StudioDashboard /></ProtectedRoute>
            } />
            <Route path="*" element={<NotFound />} />
          </Routes>
          <ClinqOnly>
            {DAVID_ENABLED && <AssistantWidget />}
            <WhatsAppFloat />
          </ClinqOnly>
        </AuthProvider>
      </BrowserRouter>
      </GoogleOAuthProvider>
    </div>
  );
}

export default App;

function VerifyRedirect() {
  const { verifyId } = useParams();
  useEffect(() => {
    const safe = (verifyId || "").replace(/[^A-Za-z0-9_-]/g, "").slice(0, 32);
    window.location.replace(
      `${process.env.REACT_APP_BACKEND_URL}/api/public/verify/${safe}`);
  }, [verifyId]);
  return (
    <div style={{minHeight:"60vh",display:"flex",alignItems:"center",justifyContent:"center",
                 color:"#7BD3CE",fontFamily:"system-ui",background:"#04302E"}}>
      <div style={{textAlign:"center"}}>
        <div style={{fontSize:14,fontWeight:800,letterSpacing:1}}>CLINQ · VERIFYING REPORT…</div>
        <div style={{marginTop:8,fontSize:12,color:"#7BD3CE99"}}>
          One moment, fetching the public registry record.
        </div>
      </div>
    </div>
  );
}
